import { z } from 'zod';

import { ERROR_MESSAGES } from '../constants/sale.constants';
import { SALE_DATE_MASK_PATTERN } from '../utils/sale-date-mask.utils';

const requiredStringField = z
  .string({ required_error: ERROR_MESSAGES.REQUIRED_FIELD })
  .min(1, ERROR_MESSAGES.REQUIRED_FIELD)
  .trim();

const requiredDateMaskField = z
  .string({ required_error: ERROR_MESSAGES.REQUIRED_FIELD })
  .min(1, ERROR_MESSAGES.REQUIRED_FIELD)
  .regex(SALE_DATE_MASK_PATTERN, ERROR_MESSAGES.INVALID_DATE);

const optionalUrlField = z.string().url(ERROR_MESSAGES.INVALID_URL).optional().or(z.literal(''));

export const saleApplicationStepOneSchema = z.object({
  name: requiredStringField,
  phone: z
    .string({ required_error: ERROR_MESSAGES.REQUIRED_FIELD })
    .min(1, ERROR_MESSAGES.REQUIRED_FIELD),
  contactLink: z
    .string({ required_error: ERROR_MESSAGES.REQUIRED_FIELD })
    .min(1, ERROR_MESSAGES.REQUIRED_FIELD)
    .url(ERROR_MESSAGES.INVALID_URL),
  profileLink: optionalUrlField,
  consent: z.boolean().refine((val) => val === true, {
    message: ERROR_MESSAGES.CONSENT_REQUIRED,
  }),
});

export const saleApplicationStepTwoSchema = z.object({
  entityType: z.string().min(1, ERROR_MESSAGES.REQUIRED_FIELD),
  lastName: requiredStringField,
  firstName: requiredStringField,
  middleName: requiredStringField,
  citizenship: z.string().min(1, ERROR_MESSAGES.REQUIRED_FIELD),
  passportNumber: z
    .string({ required_error: ERROR_MESSAGES.REQUIRED_FIELD })
    .min(1, ERROR_MESSAGES.REQUIRED_FIELD),
  passportIssuedBy: requiredStringField,
  passportIssuedDate: requiredDateMaskField,
  birthDate: requiredDateMaskField,
  bankName: requiredStringField,
  bankAccount: z
    .string({ required_error: ERROR_MESSAGES.REQUIRED_FIELD })
    .min(1, ERROR_MESSAGES.REQUIRED_FIELD)
    .regex(/^\d{20}$/, ERROR_MESSAGES.INVALID_BANK_ACCOUNT),
  bik: z
    .string({ required_error: ERROR_MESSAGES.REQUIRED_FIELD })
    .min(1, ERROR_MESSAGES.REQUIRED_FIELD)
    .regex(/^\d{9}$/, ERROR_MESSAGES.INVALID_BIK),
  recipientFullName: requiredStringField,
  recipientInn: z
    .string({ required_error: ERROR_MESSAGES.REQUIRED_FIELD })
    .min(1, ERROR_MESSAGES.REQUIRED_FIELD)
    .regex(/^\d{12}$/, ERROR_MESSAGES.INVALID_INN),
});

export const saleApplicationSchema = saleApplicationStepOneSchema.merge(
  saleApplicationStepTwoSchema,
);

export type SaleApplicationStepOneData = z.infer<typeof saleApplicationStepOneSchema>;
export type SaleApplicationStepTwoData = z.infer<typeof saleApplicationStepTwoSchema>;
export type SaleApplicationData = z.infer<typeof saleApplicationSchema>;

export const SALE_APPLICATION_STEP_ONE_FIELD_KEYS = [
  'name',
  'phone',
  'contactLink',
  'profileLink',
  'consent',
] as const satisfies ReadonlyArray<keyof SaleApplicationStepOneData>;

export const SALE_APPLICATION_STEP_TWO_FIELD_KEYS = [
  'entityType',
  'lastName',
  'firstName',
  'middleName',
  'citizenship',
  'passportNumber',
  'passportIssuedBy',
  'passportIssuedDate',
  'birthDate',
  'bankName',
  'bankAccount',
  'bik',
  'recipientFullName',
  'recipientInn',
] as const satisfies ReadonlyArray<keyof SaleApplicationStepTwoData>;

export const SALE_APPLICATION_STEP_ONE_DEFAULT_VALUES: SaleApplicationStepOneData = {
  name: '',
  phone: '',
  contactLink: '',
  profileLink: '',
  consent: false,
};

export const SALE_APPLICATION_STEP_TWO_DEFAULT_VALUES: SaleApplicationStepTwoData = {
  entityType: 'individual',
  lastName: '',
  firstName: '',
  middleName: '',
  citizenship: 'RU',
  passportNumber: '',
  passportIssuedBy: '',
  passportIssuedDate: '',
  birthDate: '',
  bankName: '',
  bankAccount: '',
  bik: '',
  recipientFullName: '',
  recipientInn: '',
};

export const SALE_APPLICATION_DEFAULT_VALUES: SaleApplicationData = {
  ...SALE_APPLICATION_STEP_ONE_DEFAULT_VALUES,
  ...SALE_APPLICATION_STEP_TWO_DEFAULT_VALUES,
};
